// src/admin/pages/RunListPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminTable from '../components/AdminTable.jsx';
import AdminPagination from '../components/AdminPagination.jsx';
import StatusBadge from '../components/StatusBadge.jsx';
import RunFilters from '../components/RunFilters.jsx';
import { listRuns } from '../runsApi.js';
import { adminListTenants } from '../adminApi.js';
import { useAuth } from '../../auth/AuthContext.jsx';

const LIMIT = 20;

function sevenDaysAgoIso() {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10); // YYYY-MM-DD
}

export default function RunListPage() {
  const navigate = useNavigate();
  const { isSuperadmin, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const offset = parseInt(searchParams.get('offset') || '0', 10);
  const urlTenant = searchParams.get('tenant') || '';
  const statusFromUrl = (searchParams.get('status') || '').split(',').filter(Boolean);
  // Default `since` to 7 days ago only when URL has no `since` param.
  const urlSince = searchParams.get('since') || sevenDaysAgoIso();
  const urlBefore = searchParams.get('before') || '';
  const urlUser = searchParams.get('user') || '';
  const urlPlatform = searchParams.get('platform') || '';
  const urlFormat = searchParams.get('content_format') || '';
  const hasActiveFilters =
    statusFromUrl.length > 0 ||
    !!urlUser || !!urlPlatform || !!urlFormat ||
    (searchParams.get('since') || '') !== '' ||
    (searchParams.get('before') || '') !== '';

  function patchParams(patch) {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === '' || v == null) next.delete(k);
      else next.set(k, String(v));
    });
    setSearchParams(next, { replace: false });
  }

  const tenantStorageKey = `runs_last_tenant:${user?.user_id || user?.email || 'anon'}`;

  const [runs, setRuns] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    if (!isSuperadmin) return;
    adminListTenants().then(setTenants).catch(() => setTenants([]));
  }, [isSuperadmin]);

  // Persist current tenant; hydrate URL from last-used on first mount when empty.
  useEffect(() => {
    if (!isSuperadmin) return;
    if (urlTenant) {
      try { localStorage.setItem(tenantStorageKey, urlTenant); } catch {}
      return;
    }
    try {
      const last = localStorage.getItem(tenantStorageKey);
      if (last) patchParams({ tenant: last });
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperadmin, urlTenant, tenantStorageKey]);

  const load = useCallback(async () => {
    if (isSuperadmin && !urlTenant) {
      setRuns([]); setTotal(0); setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await listRuns({
        tenant: urlTenant || undefined,
        status: statusFromUrl.length > 0 ? statusFromUrl : undefined,
        since: urlSince || undefined,
        before: urlBefore || undefined,
        user: urlUser || undefined,
        platform: urlPlatform || undefined,
        contentFormat: urlFormat || undefined,
        limit: LIMIT,
        offset,
      });
      setRuns(data.runs || []);
      setTotal(typeof data.total === 'number' ? data.total : (data.runs?.length || 0));
    } catch (err) {
      const msg = err.message || '';
      // Stale/invalid saved tenant → BE 403. Clear it so the next mount lets
      // the user pick a fresh tenant from the dropdown instead of looping.
      if (isSuperadmin && urlTenant && /403/.test(msg)) {
        try { localStorage.removeItem(tenantStorageKey); } catch {}
        setError(`Tenant "${urlTenant}" non accessibile — selezionane un altro dal menu.`);
        patchParams({ tenant: '' });
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperadmin, urlTenant, urlSince, urlBefore, urlUser, urlPlatform, urlFormat, offset, statusFromUrl.join(',')]);

  useEffect(() => { load(); }, [load]);

  const columns = [
    {
      key: 'topic', label: 'Topic',
      render: row => (
        <span title={row.topic || ''} style={{ display: 'inline-block', maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
          {row.topic || '—'}
        </span>
      ),
    },
    {
      key: 'platform', label: 'Platform / Format', width: '180px',
      render: row => `${row.platform || '—'}${row.content_format ? ` / ${row.content_format}` : ''}`,
    },
    { key: 'status', label: 'Stato', width: '120px', render: row => <StatusBadge status={row.status} /> },
    {
      key: 'user_email', label: 'User', width: '180px',
      render: row => row.created_by_email || row.user_email || row.user_id || '—',
    },
    ...(isSuperadmin ? [{
      key: 'tenant', label: 'Tenant', width: '160px',
      render: row => row.tenant_display_name || row.tenant_id || '—',
    }] : []),
    {
      key: 'created_at', label: 'Created', width: '140px',
      render: row => row.created_at
        ? new Date(row.created_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })
        : '—',
    },
    {
      key: 'task_id', label: 'Task ID', width: '110px',
      render: row => row.task_id ? (
        <code style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px' }}>
          {String(row.task_id).slice(-8)}
        </code>
      ) : '—',
    },
  ];

  return (
    <div style={{ padding: '32px 40px', fontFamily: "'DM Sans', sans-serif", color: '#1d1d1f', maxWidth: '1200px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>Gestione Run</h1>
        <p style={{ color: '#6c6c6d', fontSize: '13px', margin: '4px 0 0' }}>
          Cronologia delle generazioni pipeline (ultimi 7 giorni)
        </p>
      </div>

      {error && (
        <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: '8px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', color: '#EF4444', fontSize: '13px' }}>
          {error}
        </div>
      )}

      {isSuperadmin && (
        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <label style={{ fontSize: '13px', color: '#3a3a3c' }}>Tenant:</label>
          <select
            value={urlTenant}
            onChange={e => patchParams({ tenant: e.target.value, offset: '0' })}
            style={{
              padding: '6px 10px', borderRadius: '6px',
              border: '1px solid rgba(0,0,0,0.12)', background: '#fff',
              fontSize: '13px', fontFamily: "'DM Sans', sans-serif",
              minWidth: '240px',
            }}
          >
            <option value="">— Seleziona —</option>
            {tenants.map(t => (
              <option key={t.id} value={t.id}>{t.display_name || t.id}</option>
            ))}
          </select>
        </div>
      )}

      {isSuperadmin && !urlTenant ? (
        <div style={{ padding: '60px 40px', textAlign: 'center', background: '#ffffff', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.06)', color: '#6c6c6d', fontSize: '13px' }}>
          Seleziona un tenant per visualizzare le run.
        </div>
      ) : (
        <>
          <RunFilters
            status={statusFromUrl}
            since={urlSince}
            before={urlBefore}
            user={urlUser}
            platform={urlPlatform}
            contentFormat={urlFormat}
            onChange={(patch) => patchParams({ ...patch, offset: '0' })}
          />

          {hasActiveFilters && (
            <button
              type="button"
              onClick={() => {
                const next = new URLSearchParams();
                if (urlTenant) next.set('tenant', urlTenant);
                setSearchParams(next, { replace: false });
              }}
              style={{
                padding: '5px 10px', borderRadius: '6px', border: '1px solid rgba(0,0,0,0.1)',
                background: '#fff', color: '#3a3a3c', fontSize: '12px', cursor: 'pointer',
                fontFamily: "'DM Sans', sans-serif", marginBottom: '12px',
              }}
            >
              Reset filtri
            </button>
          )}

          <div style={{ background: '#ffffff', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <AdminTable
              columns={columns}
              rows={runs}
              loading={loading}
              emptyMessage={hasActiveFilters ? 'Nessuna run trovata con i filtri attuali' : 'Nessuna run negli ultimi 7 giorni'}
              onRowClick={row => navigate(`/admin/runs/${row.task_id}`, { state: { from: `/admin/runs?${searchParams}` } })}
              getRowKey={row => row.task_id}
            />
          </div>
          <AdminPagination total={total} limit={LIMIT} offset={offset} onChange={n => patchParams({ offset: n })} itemLabel="run" />
        </>
      )}
    </div>
  );
}
